import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";

import IconOnlyButton from "~/components/buttons/IconOnlyButton";

import useStringTranslation from "~/hooks/translations/useStringTranslation";

import { h3, h4 } from "~/styles/typographyStyles";

import { screenReaderText } from "~/styles/screenReaderText";

const Dialog = styled.dialog`
    width: 100%;
    padding: 6rem 35px 35px 35px;
    max-width: ${({ large, small }) =>
        large ? "1300px" : small ? "525px" : "900px"};
    max-height: 90%;
    margin: 0 auto;
    scroll-behavior: smooth;
    background-color: ${({ theme }) => theme.palette.white};
    border: 1px solid ${({ theme }) => theme.palette.borderGray};
    top: 50%;
    transform: translate(0, -50%);
    cursor: default;
    font-size: 2rem;
    line-height: 1.6;

    &::backdrop {
        background-color: ${({ backdropOverride }) =>
            backdropOverride || "rgba(255, 255, 255, 0.75)"};
        backdrop-filter: blur(2px);
    }

    &[open] {
        display: flex;
        flex-direction: column;
        gap: 3rem;
    }

    *:focus-visible,
    *:focus {
        outline: 3px solid ${({ theme }) => theme.palette.focusBlue};
        outline-offset: 5px;
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    position: absolute;
    top: 10px;
    right: 15px;
    font-size: 2.5rem;
    z-index: 100;
`;

const Header = styled.div`
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.borderGray}`};
    padding: 20px 0;
    display: flex;
    align-items: center;
`;

const Title = styled.h1`
    ${h3};
    margin: 0;
    width: 100%;
`;

const Subtitle = styled.p`
    ${h4};
    margin: 0;
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        font-size: 1.8rem;
    }
`;

const HiddenDialogHeading = styled.h2`
    ${screenReaderText}
`;

const BaseModal = ({
    children,
    formTitle = "",
    title = "",
    subtitle = "",
    onRequestClose = () => {},
    large = false,
    small = false,
    isOpen,
    shouldCloseOnEsc = true,
    shouldCloseOnOutsideClick = true,
    backdropOverride = "",
    contentStyles = {},
    showIconButton = true,
    ariaDescribedby,
    focusId = "",
}) => {
    const dialogRef = useRef(null);

    useEffect(() => {
        if (dialogRef.current) {
            if (isOpen) {
                dialogRef.current.showModal();
            } else {
                dialogRef.current.close();
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (!dialogRef.current || !isOpen) return;

        const dialog = dialogRef.current;

        if (ariaDescribedby) {
            dialog.setAttribute("aria-describedby", ariaDescribedby);
        }

        // Set the Aria Labeled By attribute.
        const heading = dialog.querySelector("h1, h2, h3, h4, h5, h6");

        if (heading) {
            const headingId = heading.getAttribute("id");
            const id = headingId || "wolfspeed-base-modal-heading-id";
            dialog.setAttribute("aria-labelledby", id);
            heading.setAttribute("id", id);
        }
    }, [isOpen, ariaDescribedby]);

    useEffect(() => {
        const dialog = dialogRef.current;

        if (dialog) {
            if (focusId) {
                const el = dialog.querySelector(focusId);

                if (el) {
                    el.focus();
                } else {
                    dialog.focus();
                }
            } else {
                dialog.focus();
            }
        }
    }, [isOpen, focusId]);

    useEffect(() => {
        const onFullscreenChange = () => {
            if (dialogRef.current && !document.fullscreenElement) {
                dialogRef.current.focus();
            }
        };

        document.addEventListener("fullscreenchange", onFullscreenChange);

        return () => {
            document.removeEventListener(
                "fullscreenchange",
                onFullscreenChange,
            );
        };
    }, []);

    useEffect(() => {
        const onKeyPress = (event) => {
            if (event.key === "Escape" && isOpen && shouldCloseOnEsc) {
                onRequestClose();
            }
        };

        document.addEventListener("keydown", onKeyPress);

        return () => {
            document.removeEventListener("keydown", onKeyPress);
        };
    }, [isOpen, shouldCloseOnEsc, onRequestClose]);

    useEffect(() => {
        const dialog = dialogRef.current;

        const onClick = (event) => {
            if (event.pointerType !== "mouse") {
                return;
            }

            const rect = dialog.getBoundingClientRect();

            const clickedInDialog =
                rect.top <= event.clientY &&
                event.clientY <= rect.top + rect.height &&
                rect.left <= event.clientX &&
                event.clientX <= rect.left + rect.width;

            if (
                !clickedInDialog &&
                shouldCloseOnOutsideClick &&
                !document.fullscreenElement
            ) {
                onRequestClose();
            }
        };

        if (dialog) {
            dialog.addEventListener("click", onClick);
        }

        return () => {
            if (dialog) {
                dialog.removeEventListener("click", onClick);
            }
        };
    }, [onRequestClose, shouldCloseOnOutsideClick]);

    const { dialogText } = useStringTranslation([
        { text: formTitle, handle: "dialogText" },
    ]);

    return (
        <Dialog
            className="wolfspeed-base-modal"
            style={contentStyles}
            ref={dialogRef}
            large={large}
            small={small}
            backdropOverride={backdropOverride}
        >
            {dialogText ? (
                <HiddenDialogHeading id="modalTitle">
                    {dialogText}
                </HiddenDialogHeading>
            ) : null}
            {showIconButton ? (
                <ButtonWrapper>
                    <IconOnlyButton
                        id="modalClose"
                        onClick={() => onRequestClose()}
                        iconLibrary="fas"
                        icon="times"
                        screenReaderText="Close"
                    />
                </ButtonWrapper>
            ) : null}
            {title ? (
                <Header>
                    <Title>{title}</Title>
                </Header>
            ) : null}
            {subtitle ? (
                <Subtitle id={ariaDescribedby}>{subtitle}</Subtitle>
            ) : null}
            {children}
        </Dialog>
    );
};

BaseModal.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    large: PropTypes.bool,
    small: PropTypes.bool,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.element),
        PropTypes.element,
    ]).isRequired,
    formTitle: PropTypes.string,
    onRequestClose: PropTypes.func,
    isOpen: PropTypes.bool.isRequired,
    shouldCloseOnEsc: PropTypes.bool,
    shouldCloseOnOutsideClick: PropTypes.bool,
    backdropOverride: PropTypes.string,
    contentStyles: PropTypes.shape({}),
    showIconButton: PropTypes.bool,
    focusId: PropTypes.string,
};

export default BaseModal;
